import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api/client';

type PreviewState = 'idle' | 'building' | 'ready' | 'error';

/**
 * Hook to manage preview build lifecycle.
 * Used by editor pages that include PreviewPanel.
 */
export function usePreview(siteId: string | undefined) {
  const [state, setState] = useState<PreviewState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [cacheBust, setCacheBust] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = useCallback(() => {
    if (!siteId) return;
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const site = await api.getSite(siteId);
        if (site.previewStatus === 'ready') {
          setState('ready');
          setCacheBust(Date.now());
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        } else if (site.previewStatus === 'error') {
          setState('error');
          setErrorMsg(site.previewErrorMessage || 'Preview build failed');
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        // Keep polling on transient errors
      }
    }, 3000);
  }, [siteId]);

  // On mount, check if a preview is already built or building — auto-build if none exists
  useEffect(() => {
    if (!siteId) return;
    (async () => {
      try {
        const site = await api.getSite(siteId);
        if (site.previewStatus === 'ready') {
          setState('ready');
          setCacheBust(Date.now());
        } else if (site.previewStatus === 'building') {
          setState('building');
          startPolling();
        } else {
          // No preview exists — auto-trigger one
          setState('building');
          try {
            await api.triggerPreview(siteId);
            startPolling();
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to build preview';
            if (msg.includes('already in progress')) {
              startPolling();
            } else {
              setState('error');
              setErrorMsg(msg);
            }
          }
        }
      } catch {
        // ignore — will start in idle state
      }
    })();
  }, [siteId, startPolling]);

  const triggerPreview = useCallback(async () => {
    if (!siteId) return;
    setState('building');
    setErrorMsg(null);
    try {
      await api.triggerPreview(siteId);
      startPolling();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to trigger preview';
      // If a build is already in progress, just poll for it
      if (msg.includes('already in progress')) {
        startPolling();
      } else {
        setState('error');
        setErrorMsg(msg);
      }
    }
  }, [siteId, startPolling]);

  const refreshIframe = useCallback(() => {
    setCacheBust(Date.now());
  }, []);

  return {
    previewState: state,
    previewError: errorMsg,
    cacheBust,
    triggerPreview,
    refreshIframe,
  };
}
